import { ArrowRight, Clock, Shield, FileText, CheckCircle2, AlertTriangle, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link, useNavigate } from "react-router-dom";
import { trackEvent } from "@/lib/analytics";
import { useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";

function setMeta(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

const FAQS: { q: string; a: string }[] = [
  { q: "Hvordan henter I tidsdata fra monday.com?", a: "Du indtaster dit monday board-ID og en API-token én gang. Time-app'en henter automatisk tids-tracking kolonner fra dit board og mapper dem til medarbejdere. Du kan re-synce når som helst — vi overskriver aldrig data, vi tilføjer kun nye registreringer." },
  { q: "Hvilke danske lovkrav dækker NordicTime?", a: "Lov om tidsregistrering (gældende fra 1. juli 2024), Arbejdstidsdirektivet (48-timers reglen pr. uge), 11-timers hviletid mellem vagter, lovpligtig pause ved >6 timers arbejde, samt 5-års opbevaringspligt. Vi hjælper jer med at stå stærkt ved et kontrolbesøg fra Arbejdstilsynet — den primære risiko ved manglende dokumentation er ikke direkte bøder, men processuel skadevirkning i en eventuel sag, hvor manglende registreringer typisk fortolkes til medarbejderens fordel." },
  { q: "Hvordan håndterer I pauser og overtid?", a: "Du sætter standard-pause (fx 30 min) og overtidsgrænse (37t/uge for funktionærer) i indstillingerne. Vi trækker pauser fra arbejdstiden og beregner overtid pr. uge. Hvis en medarbejder arbejder >6 timer uden pause, får du en BREAK_MISSING-advarsel (severity 4000)." },
  { q: "Hvad indeholder månedsrapporten?", a: "En branded PDF pr. medarbejder med: total arbejdstid, overtid, pauser, alle dage opdelt med ind-/ud-stempling, samt en oversigt over eventuelle advarsler og lovbrud. Klar til at lægge i personalemappen eller sende til lønbureau." },
  { q: "Hvor opbevares data, og hvor længe?", a: "Alle data ligger i EU (Supabase Frankfurt). Vi opbevarer registreringer i mindst 5 år som loven kræver. Du kan eksportere alt som CSV/PDF når som helst, og slette en konto med 30 dages varsel." },
  { q: "Skal vi droppe monday's egen tids-kolonne?", a: "Nej. monday's tids-kolonne fungerer fint til projekt-tracking. NordicTime lægger sig oven på som compliance-laget — vi læser jeres eksisterende registreringer og tilføjer hviletidsregler, advarsler og lovpligtige rapporter." },
];

function getIsoWeek(d: Date): { week: number; year: number } {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return { week, year: date.getUTCFullYear() };
}

export default function NordicTimeLandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { week, year } = getIsoWeek(new Date());

  useEffect(() => {
    document.title = "NordicTime — Lovpligtig tidsregistrering til monday.com";
    const desc = "Hviletidsregler, månedsrapporter og 5-års arkivering oven på monday.com. Bygget til de danske og nordiske krav.";
    setMeta("description", desc);

    // OG / Twitter
    const setProp = (prop: string, content: string) => {
      let el = document.querySelector(`meta[property="${prop}"]`) as HTMLMetaElement | null;
      if (!el) { el = document.createElement("meta"); el.setAttribute("property", prop); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    setProp("og:title", "NordicTime — Lovpligtig tidsregistrering til monday.com");
    setProp("og:description", desc);
    setProp("og:type", "website");
    setProp("og:url", "https://nordictime.app/time");
    setProp("og:image", "https://nordictime.app/og-nordictime.png");
    setProp("og:locale", "da_DK");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "NordicTime — Lovpligtig tidsregistrering til monday.com");
    setMeta("twitter:description", desc);
    setMeta("twitter:image", "https://nordictime.app/og-nordictime.png");

    // Canonical
    let canon = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canon) { canon = document.createElement("link"); canon.setAttribute("rel", "canonical"); document.head.appendChild(canon); }
    canon.setAttribute("href", "https://nordictime.app/time");

    // JSON-LD SoftwareApplication
    const ldId = "ld-nordictime-app";
    document.getElementById(ldId)?.remove();
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.id = ldId;
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "NordicTime",
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web, monday.com",
      description: desc,
      url: "https://nordictime.app/time",
      offers: {
        "@type": "Offer",
        price: "299",
        priceCurrency: "DKK",
        priceSpecification: { "@type": "UnitPriceSpecification", price: "299", priceCurrency: "DKK", referenceQuantity: { "@type": "QuantitativeValue", unitCode: "MON" } },
      },
      provider: { "@type": "Organization", name: "AddonNordic", url: "https://addonnordic.dk" },
    });
    document.head.appendChild(ld);

    // FAQPage JSON-LD
    const faqId = "ld-nordictime-faq";
    document.getElementById(faqId)?.remove();
    const faq = document.createElement("script");
    faq.type = "application/ld+json";
    faq.id = faqId;
    faq.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQS.map(({ q, a }) => ({
        "@type": "Question",
        name: q,
        acceptedAnswer: { "@type": "Answer", text: a },
      })),
    });
    document.head.appendChild(faq);

    return () => {
      document.getElementById(ldId)?.remove();
      document.getElementById(faqId)?.remove();
    };
  }, []);

  const goToApp = () => {
    trackEvent("time_cta_click", { label: "hero", authed: !!user });
    if (user) navigate("/app/time");
    else navigate("/auth?redirect=/app/time");
  };

  const ctaLabel = user ? "Åbn NordicTime" : "Log ind og kom i gang";

  return (
    <main className="min-h-screen bg-background">
      <header className="border-b">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4">
          <Link to="/" className="text-lg font-semibold tracking-tight">
            AddonNordic <span className="text-muted-foreground font-normal">· NordicTime</span>
          </Link>
          <nav className="flex items-center gap-4 text-sm">
            <Link to="/auth" className="text-muted-foreground hover:text-foreground">
              Log ind
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-3xl px-5 py-20 text-center">
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
          Spor B · Gratis op til 5 ansatte
        </div>
        <h1 className="mt-6 text-4xl font-bold tracking-tight sm:text-5xl">
          Stå stærkt ved Arbejdstilsynets kontrolbesøg.
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">
          Tidsregistrering oven på monday.com — bygget til at dokumentere, at I behandler
          medarbejderne rigtigt. Audit-trail, hviletidsregler og månedsrapporter klar til
          revision og personalemappe.
        </p>
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          <Button
            size="lg"
            className="h-auto min-h-[52px] rounded-full px-8 py-3.5 text-base font-semibold"
            onClick={goToApp}
          >
            {ctaLabel}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
          <Button asChild size="lg" variant="outline" className="h-auto min-h-[52px] rounded-full px-8 py-3.5">
            <Link to="/time/pricing">Se priser</Link>
          </Button>
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Gratis for de første 5 ansatte · Ingen kreditkort · Dansk support
        </p>
      </section>

      {/* Preview / screenshot */}
      <section className="mx-auto max-w-5xl px-5 pb-16">
        <div className="rounded-2xl border bg-card p-2 shadow-2xl">
          <div className="rounded-xl bg-muted/30 p-6 sm:p-10">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase text-muted-foreground">Uge {week} · {year}</p>
                <p className="text-2xl font-semibold">Anders Hansen</p>
              </div>
              <div className="flex gap-2">
                <span className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  37t 0m
                </span>
                <span className="rounded-md bg-amber-500/10 px-3 py-1 text-xs font-medium text-amber-600">
                  +2t 30m OT
                </span>
              </div>
            </div>
            <div className="space-y-2">
              {[
                { day: "Man 04/05", hours: "08:00–16:30", worked: "8t 0m", warn: null },
                { day: "Tir 05/05", hours: "07:30–17:00", worked: "9t 0m", warn: "BREAK_MISSING" },
                { day: "Ons 06/05", hours: "08:00–16:00", worked: "7t 30m", warn: null },
                { day: "Tor 07/05", hours: "08:00–18:30", worked: "10t 0m", warn: null },
                { day: "Fre 08/05", hours: "08:00–14:30", worked: "6t 0m", warn: null },
              ].map((row) => (
                <div
                  key={row.day}
                  className="flex items-center justify-between rounded-md bg-background px-4 py-2.5 text-sm"
                >
                  <span className="font-medium">{row.day}</span>
                  <span className="text-muted-foreground">{row.hours}</span>
                  <span className="font-mono">{row.worked}</span>
                  {row.warn ? (
                    <span className="inline-flex items-center gap-1 rounded bg-amber-500/10 px-2 py-0.5 text-xs text-amber-600">
                      <AlertTriangle className="h-3 w-3" />
                      Manglende pause
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                      OK
                    </span>
                  )}
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center justify-between border-t pt-4">
              <p className="text-xs text-muted-foreground">
                Genereret fra monday-board · auto-flagget efter DK arbejdstidsregler
              </p>
              <Button size="sm" variant="outline">
                <FileText className="mr-1 h-3 w-3" />
                PDF-rapport
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto grid max-w-5xl gap-6 px-5 pb-20 md:grid-cols-3">
        {[
          {
            icon: Shield,
            title: "Hviletidsregler",
            body: "Automatisk flagning af 11-timers reglen, 48-timers reglen og søn-/helligdagsregler.",
          },
          {
            icon: FileText,
            title: "Månedsrapport-PDF",
            body: "Lovpligtig dokumentation til personalemappen. Genereres med ét klik pr. medarbejder.",
          },
          {
            icon: Clock,
            title: "5-års arkivering",
            body: "Digital opbevaring i revisionssikker form. Audit-trail med tidsstempler og signaturer.",
          },
          {
            icon: Calendar,
            title: "Auto-sync fra monday",
            body: "Hent tidsregistreringer direkte fra dine eksisterende boards. Ingen dobbeltindtastning.",
          },
          {
            icon: AlertTriangle,
            title: "Lovbrud-alarmer",
            body: "Bliv advaret ved >13t/dag, manglende pauser og brud på 11-timers hvile mellem vagter.",
          },
          {
            icon: CheckCircle2,
            title: "Bygget til Norden",
            body: "Danske defaults out-of-the-box. Norge & Sverige følger med samme regelmotor.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <Card key={title} className="p-6">
            <Icon className="h-6 w-6 text-primary" />
            <h3 className="mt-4 text-lg font-semibold">{title}</h3>
            <p className="mt-2 text-sm text-muted-foreground">{body}</p>
          </Card>
        ))}
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-5xl px-5 pb-20">
        <h2 className="text-center text-3xl font-bold tracking-tight">Hvor passer vi ind?</h2>
        <p className="mx-auto mt-3 max-w-2xl text-center text-muted-foreground">
          Det danske marked har mange gode tidsregistreringsløsninger. Her er hvor NordicTime
          adskiller sig — og hvornår en anden løsning måske passer bedre.
        </p>
        <div className="mt-8 overflow-x-auto rounded-xl border bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-medium">Egenskab</th>
                <th className="px-4 py-3 font-medium text-primary">NordicTime</th>
                <th className="px-4 py-3 font-medium">Tidsmester.dk</th>
                <th className="px-4 py-3 font-medium">TimeLog</th>
                <th className="px-4 py-3 font-medium">Clockify</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {[
                ["monday.com-integration", "Ja, board-iframe", "Nej", "Nej", "Nej"],
                ["Dansk 2024-lov indbygget", "Ja", "Ja", "Tilgængelig", "Nej"],
                ["Audit-trail med severity-koder", "Ja (4000/6000)", "Basis", "Ja", "Nej"],
                ["Authority Export", "PDF + CSV + XML", "PDF", "PDF", "Manuel"],
                ["Pris pr. ansat/md", "39 kr (5 gratis)", "Fra 10 kr", "Fra ~150 kr", "Gratis"],
                ["Bedst til", "monday-brugere, SMV 5-50", "Bredt SMV uden monday", "Konsulenthuse", "Freelancere"],
              ].map((row, i) => (
                <tr key={i}>
                  <td className="px-4 py-3 font-medium">{row[0]}</td>
                  <td className="bg-primary/5 px-4 py-3 font-medium text-foreground">{row[1]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[2]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[3]}</td>
                  <td className="px-4 py-3 text-muted-foreground">{row[4]}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mx-auto mt-4 max-w-2xl text-center text-xs text-muted-foreground">
          Tabellen er baseret på offentligt tilgængelig produktinformation (maj 2026) og er ment
          som en hjælp — ikke som en vurdering. Bruger I monday.com og er 5–50 ansatte? Så er vi
          lavet til jer.
        </p>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-3xl px-5 pb-20">
        <h2 className="text-center text-3xl font-bold tracking-tight">Ofte stillede spørgsmål</h2>
        <p className="mx-auto mt-3 max-w-xl text-center text-muted-foreground">
          Det vi oftest bliver spurgt om monday-sync, lovkrav og rapporter.
        </p>
        <Accordion type="single" collapsible className="mt-8 w-full">
          {FAQS.map((item, i) => (
            <AccordionItem key={i} value={`item-${i}`}>
              <AccordionTrigger className="text-left text-base font-semibold">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground">{item.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      {/* Bottom CTA */}
      <section className="mx-auto max-w-3xl px-5 pb-24 text-center">
        <Card className="p-10">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Klar til at komme i gang?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
            Log ind, forbind dit monday-board og få din første lovlige månedsrapport på under 5 minutter.
          </p>
          <Button
            size="lg"
            className="mt-6 h-auto min-h-[52px] rounded-full px-8 py-3.5 text-base font-semibold"
            onClick={goToApp}
          >
            {ctaLabel}
            <ArrowRight className="ml-1 h-4 w-4" />
          </Button>
        </Card>
      </section>

      <footer className="border-t">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-4 py-6 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} AddonNordic</p>
          <nav className="flex flex-wrap gap-4">
            <Link to="/time/pricing">Priser</Link>
            <Link to="/privacy">Privatliv</Link>
            <Link to="/terms">Vilkår</Link>
            <Link to="/refund">Refundering</Link>
            <Link to="/trust">Trust Center</Link>
          </nav>
        </div>
      </footer>
    </main>
  );
}
